import { Button, Typography, Descriptions, Alert, Space, Spin } from 'antd';
import {
  HomeOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'libs/dayjs';
import { useState, useEffect } from 'react';
import { useSolicitudesMantencion } from 'providers/SolicitudesMantencionProvider';
import { captureGpsDetailed } from 'hooks/useVisita';
import MapaUbicacion from 'components/mantenciones/MapaUbicacion';

const { Text, Paragraph } = Typography;

const mesActualLabel = () => dayjs().format('MMMM YYYY');

const InicioMantencionConfirm = ({ solicitud, onCancel }) => {
  const { iniciarMantencionConfirmada } = useSolicitudesMantencion();
  const [loading, setLoading] = useState(false);
  // Estado de error inline: null | { parcial, bloqueante, mensaje, ascensoresSinPlan }
  const [warn, setWarn] = useState(null);
  // Ubicación GPS capturada al montar (best-effort).
  const [gps, setGps] = useState({ loading: false, lat: null, lng: null, ok: false, reason: null });

  const cliente = solicitud?.Cliente;

  useEffect(() => {
    if (!solicitud) return undefined;
    let cancelado = false;
    setGps({ loading: true, lat: null, lng: null, ok: false, reason: null });
    captureGpsDetailed().then((res) => {
      if (cancelado) return;
      setGps({ loading: false, lat: res.lat, lng: res.lng, ok: res.ok, reason: res.reason });
    });
    return () => {
      cancelado = true;
    };
  }, [solicitud]);

  const ejecutar = async (confirm) => {
    if (!solicitud) return;
    setLoading(true);
    setWarn(null);
    const res = await iniciarMantencionConfirmada(solicitud, {
      confirm,
      lat: gps.lat,
      lng: gps.lng,
    });
    setLoading(false);

    if (res.ok) {
      onCancel?.();
      return;
    }
    if (res.status === 409 && res.code === 'SIN_PLAN_PARCIAL') {
      setWarn({
        parcial: true,
        bloqueante: false,
        mensaje: res.data?.mensaje,
        ascensoresSinPlan: res.data?.ascensoresSinPlan || [],
      });
      return;
    }
    // 400 (CLIENTE_SIN_ASCENSORES / SIN_PLAN_TOTAL) u otro → bloqueante
    setWarn({
      parcial: false,
      bloqueante: true,
      mensaje:
        res.data?.mensaje || 'No se pudo iniciar la mantención. Revise los datos del cliente.',
      ascensoresSinPlan: res.data?.ascensoresSinPlan || [],
    });
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="N° Solicitud">#{solicitud?.id}</Descriptions.Item>
        <Descriptions.Item
          label={
            <span>
              <HomeOutlined /> Cliente
            </span>
          }
        >
          <span style={{ textTransform: 'capitalize' }}>{cliente?.name || '—'}</span>
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <span>
              <EnvironmentOutlined /> Dirección
            </span>
          }
        >
          <span style={{ textTransform: 'capitalize' }}>
            {[cliente?.address, cliente?.commune].filter(Boolean).join(', ') || '—'}
          </span>
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <span>
              <ClockCircleOutlined /> Inicio
            </span>
          }
        >
          Hoy {dayjs().format('HH:mm')}
        </Descriptions.Item>
      </Descriptions>

      <Paragraph type="secondary" style={{ margin: 0 }}>
        Se generará la mantención de <Text strong>{mesActualLabel()}</Text>, la solicitud quedará{' '}
        <Text strong>en curso</Text> y pasarás directo al checklist.
      </Paragraph>

      <div>
        {gps.loading ? (
          <Space>
            <Spin size="small" />
            <Typography.Text type="secondary">Obteniendo tu ubicación…</Typography.Text>
          </Space>
        ) : gps.ok ? (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <MapaUbicacion lat={gps.lat} lng={gps.lng} />
            <Typography.Text type="secondary">
              El inicio de la mantención quedará registrado desde esta ubicación.
            </Typography.Text>
          </Space>
        ) : (
          <Alert
            type="info"
            showIcon
            message="Sin ubicación"
            description="No se pudo obtener la ubicación. Puede continuar; el inicio no quedará georreferenciado."
          />
        )}
      </div>

      {warn?.parcial && (
        <Alert
          type="warning"
          showIcon
          message="Algunos ascensores no tienen plan este mes"
          description={
            <div>
              <div>{warn.mensaje}</div>
              {warn.ascensoresSinPlan.length > 0 && (
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {warn.ascensoresSinPlan.map((a) => (
                    <li key={a.id}>{a.nombre}</li>
                  ))}
                </ul>
              )}
            </div>
          }
        />
      )}

      {warn?.bloqueante && (
        <Alert type="error" showIcon message="No se puede iniciar" description={warn.mensaje} />
      )}

      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button onClick={() => onCancel?.()} disabled={loading}>
          Cancelar
        </Button>
        {warn?.parcial ? (
          <Button
            type="primary"
            danger
            loading={loading}
            icon={<PlayCircleOutlined />}
            onClick={() => ejecutar(true)}
          >
            Iniciar de todos modos
          </Button>
        ) : (
          <Button
            type="primary"
            loading={loading}
            disabled={warn?.bloqueante}
            icon={<PlayCircleOutlined />}
            onClick={() => ejecutar(false)}
          >
            Confirmar e iniciar
          </Button>
        )}
      </Space>
    </Space>
  );
};

export default InicioMantencionConfirm;
